import { Clock, AlertTriangle, CheckCircle, Timer } from 'lucide-react'
import { useState, useEffect } from 'react'

import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'

import {
  useTimeConfig,
  getStageEstimateDisplayName,
  getTimeStatusColor,
  getTimeStatusBgColor,
  getTimeStatusDisplayName,
  calculateDeadlineCountdown,
  formatCountdown,
  type StageTimeStatus,
  type StageEstimateResult          } from '@/hooks'
import { cn } from '@/lib/utils'
import type {
 WorkflowStage } from '@/hooks';

interface DeadlineDisplayProps {
  issueId: string
  currentStage?: WorkflowStage
  showAllStages?: boolean
  compact?: boolean
}

/**
 * 截止日期显示组件
 * Requirements: 3.4, 4.5, 6.3, 6.4
 */
export function DeadlineDisplay({
  issueId,
  currentStage,
  showAllStages = false,
  compact = false,
}: DeadlineDisplayProps) {
  const { data: timeConfig, isLoading } = useTimeConfig(issueId)

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">
        <Clock className="inline h-4 w-4 animate-spin mr-1" />
        加载中...
      </div>
    )
  }

  if (!timeConfig || timeConfig.stageEstimates.length === 0) {
    return null
  }

  // 获取当前阶段的估算
  const currentEstimate = currentStage
    ? timeConfig.stageEstimates.find((e) => e.stage === currentStage)
    : null

  if (compact) {
    return (
      <CompactDeadlineDisplay
        estimate={currentEstimate}
        finalDeadline={timeConfig.finalDeadline}
      />
    )
  }

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <Timer className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-medium">截止日期</h4>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* 最终截止日期 */}
        {timeConfig.finalDeadline && (
          <FinalDeadlineCard deadline={timeConfig.finalDeadline} />
        )}

        {/* 当前阶段截止日期 */}
        {currentEstimate && (
          <StageDeadlineCard
            estimate={currentEstimate}
            isCurrent={true}
            alertThreshold={timeConfig.alertThresholdPercent}
          />
        )}

        {/* 所有阶段截止日期 */}
        {showAllStages && (
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-muted-foreground">各阶段截止日期</h5>
            <div className="space-y-2">
              {timeConfig.stageEstimates
                .filter((e) => e.deadline)
                .map((estimate) => (
                  <StageDeadlineCard
                    key={estimate.id}
                    estimate={estimate}
                    isCurrent={estimate.stage === currentStage}
                    alertThreshold={timeConfig.alertThresholdPercent}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * 紧凑版截止日期显示
 */
function CompactDeadlineDisplay({
  estimate,
  finalDeadline,
}: {
  estimate: StageEstimateResult | null | undefined
  finalDeadline: string | null
}) {
  const [countdown, setCountdown] = useState<number | null>(null)

  // 优先显示当前阶段截止日期，否则显示最终截止日期
  const deadline = estimate?.deadline ?? finalDeadline

  useEffect(() => {
    if (!deadline) {return}

    const updateCountdown = () => {
      setCountdown(calculateDeadlineCountdown(deadline))
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 60000) // 每分钟更新

    return () => clearInterval(interval)
  }, [deadline])

  if (!deadline) {
    return null
  }

  const isOverdue = countdown !== null && countdown < 0
  const isApproaching = countdown !== null && countdown > 0 && countdown < 24 * 60 * 60 * 1000 // 24小时内

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs',
        isOverdue && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        isApproaching && !isOverdue && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        !isOverdue && !isApproaching && 'bg-muted text-muted-foreground'
      )}
    >
      {isOverdue ? (
        <AlertTriangle className="h-3 w-3" />
      ) : (
        <Clock className="h-3 w-3" />
      )}
      <span>{formatCountdown(countdown)}</span>
    </div>
  )
}

/**
 * 最终截止日期卡片
 */
function FinalDeadlineCard({ deadline }: { deadline: string }) {
  const [countdown, setCountdown] = useState<number | null>(null)

  useEffect(() => {
    const updateCountdown = () => {
      setCountdown(calculateDeadlineCountdown(deadline))
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 60000)

    return () => clearInterval(interval)
  }, [deadline])

  const isOverdue = countdown !== null && countdown < 0

  return (
    <div
      className={cn(
        'rounded-md p-3',
        isOverdue
          ? 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
          : 'bg-muted/50'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isOverdue ? (
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
          ) : (
            <Timer className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium">最终截止日期</span>
        </div>
        <Badge variant={isOverdue ? 'destructive' : 'secondary'}>
          {formatCountdown(countdown)}
        </Badge>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {new Date(deadline).toLocaleString()}
      </p>
    </div>
  )
}

/**
 * 阶段截止日期卡片
 */
function StageDeadlineCard({
  estimate,
  isCurrent,
  alertThreshold,
}: {
  estimate: StageEstimateResult
  isCurrent: boolean
  alertThreshold: number
}) {
  const [countdown, setCountdown] = useState<number | null>(null)
  const [progress, setProgress] = useState<number>(0)

  useEffect(() => {
    if (!estimate.deadline) {return}

    const updateState = () => {
      const countdownMs = calculateDeadlineCountdown(estimate.deadline)
      setCountdown(countdownMs)

      // 计算进度百分比
      if (estimate.stageStartedAt && estimate.deadline) {
        const startTime = new Date(estimate.stageStartedAt).getTime()
        const deadlineTime = new Date(estimate.deadline).getTime()
        const now = Date.now()
        const totalDuration = deadlineTime - startTime
        const elapsed = now - startTime
        const percent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100))
        setProgress(percent)
      }
    }

    updateState()
    const interval = setInterval(updateState, 60000)

    return () => clearInterval(interval)
  }, [estimate.deadline, estimate.stageStartedAt])

  if (!estimate.deadline) {
    return null
  }

  const statusColor = getTimeStatusColor(estimate.timeStatus)
  const statusBgColor = getTimeStatusBgColor(estimate.timeStatus)

  return (
    <div
      className={cn(
        'rounded-md border p-3',
        isCurrent && 'ring-2 ring-primary/20',
        estimate.timeStatus === 'overdue' && 'border-red-200 dark:border-red-800'
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <StageStatusIcon status={estimate.timeStatus} />
          <span className="text-sm font-medium">
            {getStageEstimateDisplayName(estimate.stage)}
          </span>
          {isCurrent && (
            <Badge variant="outline" className="text-xs">
              当前
            </Badge>
          )}
        </div>
        <Badge className={cn('text-xs', statusBgColor, statusColor)}>
          {getTimeStatusDisplayName(estimate.timeStatus)}
        </Badge>
      </div>

      {/* 进度条 */}
      {estimate.stageStartedAt && !estimate.stageCompletedAt && (
        <div className="mb-2">
          <Progress
            value={progress}
            className={cn(
              'h-1.5',
              estimate.timeStatus === 'overdue' && '[&>div]:bg-red-500',
              estimate.timeStatus === 'approaching_deadline' && '[&>div]:bg-yellow-500'
            )}
          />
          <div className="flex justify-between mt-1 text-xs text-muted-foreground">
            <span>已用 {progress.toFixed(0)}%</span>
            <span>阈值 {alertThreshold}%</span>
          </div>
        </div>
      )}

      {/* 截止日期和倒计时 */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          截止: {new Date(estimate.deadline).toLocaleString()}
        </span>
        <span className={statusColor}>{formatCountdown(countdown)}</span>
      </div>

      {/* 已完成状态 */}
      {estimate.stageCompletedAt && (
        <div className="mt-2 flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <CheckCircle className="h-3 w-3" />
          <span>
            已完成于 {new Date(estimate.stageCompletedAt).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * 阶段状态图标
 */
function StageStatusIcon({ status }: { status: StageTimeStatus }) {
  switch (status) {
    case 'overdue':
      return <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
    case 'approaching_deadline':
      return <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
    case 'on_track':
    default:
      return <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
  }
}

/**
 * 单行截止日期倒计时显示
 * 用于任务列表等紧凑场景
 * Requirements: 6.3, 6.4
 */
export function DeadlineCountdownBadge({
  deadline,
  timeStatus,
  className,
}: {
  deadline: string | null
  timeStatus?: StageTimeStatus
  className?: string
}) {
  const [countdown, setCountdown] = useState<number | null>(null)

  useEffect(() => {
    if (!deadline) {return}

    const updateCountdown = () => {
      setCountdown(calculateDeadlineCountdown(deadline))
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 60000)

    return () => clearInterval(interval)
  }, [deadline])

  if (!deadline) {
    return null
  }

  const isOverdue = countdown !== null && countdown < 0
  const isApproaching = countdown !== null && countdown > 0 && countdown < 24 * 60 * 60 * 1000

  // 使用传入的 timeStatus 或根据倒计时计算
  const effectiveStatus: StageTimeStatus = timeStatus ?? (isOverdue ? 'overdue' : isApproaching ? 'approaching_deadline' : 'on_track')

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs',
        getTimeStatusBgColor(effectiveStatus),
        getTimeStatusColor(effectiveStatus),
        className
      )}
    >
      {effectiveStatus === 'overdue' ? (
        <AlertTriangle className="h-3 w-3" />
      ) : effectiveStatus === 'approaching_deadline' ? (
        <Clock className="h-3 w-3" />
      ) : (
        <Timer className="h-3 w-3" />
      )}
      {formatCountdown(countdown)}
    </span>
  )
}
